import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { CustomPaging, DataTypeProvider, PagingState, SortingState, SelectionState } from '@devexpress/dx-react-grid'
import {
    Grid,
    Table,
    TableHeaderRow,
    PagingPanel,
    TableSelection,
    TableColumnResizing,
    TableColumnVisibility,
} from '@devexpress/dx-react-grid-material-ui'
import { useSnackbar } from 'notistack'
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Link from '@mui/material/Link'
import { Paper, useMediaQuery } from '@mui/material'
import { getTeacherById, deleteTeacher } from 'app/redux/store/features/teachers'
import { formatDateTime } from 'app/services/utils/components/FormatDateTime/FormatDateTime'
import NoDataCellWithLoading from '../../../../services/utils/components/NoDataCellWithLoading'
import useJumboTheme from '../../../../../@jumbo/hooks/useJumboTheme'
import LoadingCircle from 'app/services/utils/components/LoadingCircle/LoadingCircle'
import GenderTypeService from 'app/utils/components/GenderType/GenderTypeService'

const TeacherGridData = ({ onPageChange, onColumnSort, setCurrentPage, currentPage, handleError }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { showDialog, hideDialog } = useJumboDialog()
    const dateTimeColumns = ['createTime', 'updateTime']
    const booleanColumns = ['isActive']
    const [sorting, setSorting] = useState([{ columnName: 'firstName', direction: 'asc' }])
    const pageSize = 25
    const { teachers, loading, error, refreshing } = useSelector((state) => state.teachers)
    const { theme } = useJumboTheme()
    const sm = useMediaQuery(theme.breakpoints.down('sm'))
    const mddown = useMediaQuery(theme.breakpoints.down('md'))
    const md = useMediaQuery(theme.breakpoints.between('md', 'xl'))
    const min = sm || mddown || md
    const [apiCallInProgress, setApiCallInProgress] = useState(false)

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
        else {
            enqueueSnackbar('Teacher data removed successfully', {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }, [loading])

    const onCurrentPageChange = (pageNumber) => {
        onPageChange(pageNumber)
        setCurrentPage(pageNumber)
    }
    const onSortingChange = (sortOrder) => {
        onColumnSort(sortOrder)
        setSorting(sortOrder)
    }

    const onNavigatingToDetail = (id) => {
        dispatch(getTeacherById({ id: id }))
        navigate(`/security/teachers/edit/${id}`, { state: { teacherId: id } })
    }

    const TableRow = ({ tableRow, ...restProps }) => {
        return (
            <TableSelection.Row
                {...restProps}
                onDoubleClick={() => {
                    onNavigatingToDetail(tableRow.row.id)
                }}
            />
        )
    }

    const NoDataCellWithLoadingType = (props) => <NoDataCellWithLoading loading={loading} {...props} />
    const DateTimeTypeProvider = (props) => <DataTypeProvider formatterComponent={formatDateTime} {...props} />

    const renderActionCell = (row) => {
        return (
            <div>
                <IconButton>
                    <EditIcon
                        sx={{ color: 'primary.main', fontSize: '18px' }}
                        onClick={() => {
                            onNavigatingToDetail(row.id)
                        }}
                    />
                </IconButton>
                <IconButton>
                    <DeleteIcon
                        sx={{ color: 'primary.main', fontSize: '18px' }}
                        onClick={() => onRemoveTeacher(row.id)}
                    />
                </IconButton>
            </div>
        )
    }

    const renderLinkCell = (row) => {
        return (
            <Link
                variant='text'
                underline='none'
                onClick={() => onNavigatingToDetail(row.id)}
                sx={{
                    '&:hover': {
                        color: 'action.active', // Change this to the color you want on hover
                        cursor: 'pointer',
                    },
                }}>
                {row.firstName}
            </Link>
        )
    }

    const BooleanFormatter = ({ value }) => <span>{formatBooleanValue(value)}</span>
    const formatBooleanValue = (value) => (value ? 'Yes' : 'No')

    const BooleanTypeProvider = (props) => <DataTypeProvider formatterComponent={BooleanFormatter} {...props} />

    const onRemoveTeacher = async (id) => {
        showDialog({
            variant: 'confirm',
            title: 'This will remove the selected record, do you want to continue?',
            onYes: async () => {
                setApiCallInProgress(true)
                dispatch(deleteTeacher({ id: id }))
                hideDialog()
            },
            onNo: hideDialog,
        })
    }

    const columns = [
        { name: 'firstName', title: 'First Name', getCellValue: renderLinkCell },
        { name: 'lastName', title: 'Last Name' },
        { name: 'genderType', title: 'Gender', getCellValue: GenderTypeService.getGenderTypeDataForAccount },
        { name: 'phoneNumber', title: 'Phone No' },
        { name: 'email', title: 'Email' },
        { name: 'comments', title: 'Comments' },
        { name: 'createTime', title: 'Created Date' },
        { name: 'updateTime', title: 'Updated Date' },
        { name: 'isActive', title: 'Enabled' },
        { name: 'Action', title: 'Action', getCellValue: renderActionCell },
    ]

    const columnWidth = useMemo(
        () => [
            { columnName: 'firstName', width: min ? '110px' : '10%', wordWrapEnabled: true, minWidth: '110px' },
            { columnName: 'lastName', width: min ? '110px' : '10%', wordWrapEnabled: true, minWidth: '110px' },
            { columnName: 'genderType', width: min ? '80px' : '7%', wordWrapEnabled: true, minWidth: '80px' },
            { columnName: 'phoneNumber', width: min ? '120px' : '8%', wordWrapEnabled: true, minWidth: '120px' },
            { columnName: 'email', width: min ? '200px' : '15%', wordWrapEnabled: true, minWidth: '200px' },
            { columnName: 'comments', width: min ? '150px' : '13%', wordWrapEnabled: true, minWidth: '150px' },
            { columnName: 'createTime', width: min ? '110px' : '12%', wordWrapEnabled: true, minWidth: '120px' },
            { columnName: 'updateTime', width: min ? '110px' : '12%', wordWrapEnabled: true, minWidth: '120px' },
            {
                columnName: 'isActive',
                width: min ? '100px' : '7%',
                wordWrapEnabled: true,
                minWidth: '100px',
                align: 'center',
            },
            {
                columnName: 'Action',
                width: min ? '90px' : '6%',
                wordWrapEnabled: true,
                minWidth: '90px',
                align: 'center',
            },
        ],
        [min]
    )
    const [columnWidths, setColumnWidths] = useState(columnWidth)

    useEffect(() => {
        setColumnWidths(columnWidth)
    }, [min])

    const sortingColumnExtensions = [
        { columnName: 'Action', sortingEnabled: false },
        { columnName: 'genderType', sortingEnabled: false },
    ]

    return (
        <Paper style={{ position: 'relative' }}>
            <Grid rows={teachers.data} columns={columns}>
                {teachers.totalCount !== 0 && <LoadingCircle refreshing={refreshing} />}
                <DateTimeTypeProvider for={dateTimeColumns} />
                <BooleanTypeProvider for={booleanColumns} />
                <SortingState
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    columnSortingEnabled={true}
                    columnExtensions={sortingColumnExtensions}
                />
                <PagingState currentPage={currentPage} onCurrentPageChange={onCurrentPageChange} pageSize={pageSize} />
                <CustomPaging totalCount={teachers.totalCount} />
                <Table columnExtensions={columnWidths} noDataCellComponent={NoDataCellWithLoadingType} />
                <TableColumnResizing
                    columnWidths={columnWidths}
                    onColumnWidthsChange={setColumnWidths}
                    defaultColumnWidths={columnWidths}
                    resizingMode='nextColumn'
                    columnExtensions={columnWidths}
                />
                <TableColumnVisibility />
                <TableHeaderRow showSortingControls />
                <SelectionState />
                <TableSelection selectByRowClick rowComponent={TableRow} showSelectionColumn={false} />
                <PagingPanel />
            </Grid>
        </Paper>
    )
}

export default TeacherGridData
